import {
  CreateBrazilianKycInputDto,
  CreateIdentityKycDto,
  CreateKycInputDto,
  CreateKycRequestDto,
} from '../../../dto';
import { KycIdentityOriginId } from '../../../enums';

/**
 * Function to compose the KYC submission input based on the origin ID.
 * This function takes an origin ID and a data object, and returns a partial KYC input object
 * tailored to the specific origin's requirements.
 *
 * @param {string} originId - The origin ID representing the country or region for the KYC process.
 * @param {CreateKycRequestDto} data - The data object containing the necessary information for KYC submission.
 * @returns {Partial<CreateKycInputDto>} - A partial KYC input object with fields populated based on the origin ID.
 *
 * Example usage:
 * const originId = 'Brazil';
 * const data: CreateKycRequestDto = { identityId: '12345', ...otherData };
 * const kycInput = getComposedKycSubmissionInput(originId, data);
 */
export function getComposedKycSubmissionInput(
  originId: string,
  data: CreateKycRequestDto,
): Partial<CreateKycInputDto> {
  // Initialize the input object with the origin ID
  let input: Partial<CreateKycInputDto> = {
    originId: originId as string,
  };

  // Determine the specific KYC input structure based on the origin ID
  switch (originId?.toString()) {
    case KycIdentityOriginId.Brazil?.toString():
      // For Brazil, set the identityId in the input
      (input as CreateBrazilianKycInputDto).identityId = data.identityId;
      break;
    case KycIdentityOriginId.India?.toString():
    case KycIdentityOriginId.Argentina?.toString():
    case KycIdentityOriginId.Mexico?.toString():
    case KycIdentityOriginId.Indonesia?.toString():
    case KycIdentityOriginId.Peru?.toString():
    case KycIdentityOriginId.SouthAfrica?.toString():
    case KycIdentityOriginId.Nigeria?.toString():
    case KycIdentityOriginId.Singapore?.toString():
      // For other specified countries, set the identity object in the input
      const kycInput = input as CreateIdentityKycDto;
      kycInput.identity = (data as CreateIdentityKycDto).identity;
      if (kycInput.identity) {
        // Ensure the identity object has the correct origin ID
        kycInput.identity.originId = originId?.toString();
      }
      break;
  }
  // Return the composed KYC input object
  return input;
}
